// pages/admin/ReportsList.tsx
import React, { useState, useMemo } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  Search,
  FilterList,
  Delete,
  Close,
  Assessment,
  PictureAsPdf,
  TableChart,
  Article,
  KeyboardArrowDown,
  KeyboardArrowUp,
  PlayArrow,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListReportsQuery,
  useGenerateReportMutation,
  useDeleteReportMutation,
  GeneratedReport,
  REPORT_TYPE_FILTER_OPTIONS,
  REPORT_STATUS_OPTIONS,
} from '../../features/admin/generatedReportsApi.ts';

import { useListReportFormatsQuery } from '../../features/admin/reportListApi.ts';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getExportIcon = (type: string) => {
  const t = (type || '').toLowerCase();
  if (t === 'pdf')   return <PictureAsPdf sx={{ fontSize: 14 }} />;
  if (t === 'csv')   return <TableChart   sx={{ fontSize: 14 }} />;
  return <Article sx={{ fontSize: 14 }} />;
};

const getExportColor = (type: string) => {
  const t = (type || '').toLowerCase();
  if (t === 'pdf')  return { bgcolor: '#ffebee', color: '#c62828' };
  if (t === 'csv')  return { bgcolor: '#e8f5e9', color: '#2e7d32' };
  return { bgcolor: '#e3f2fd', color: '#1565c0' };
};

const getStatusIcon = (status: string) => {
  if (status === 'ready')      return <CheckCircle   sx={{ fontSize: 14 }} />;
  if (status === 'failed')     return <ErrorIcon     sx={{ fontSize: 14 }} />;
  return <HourglassEmpty sx={{ fontSize: 14 }} />;
};

const getStatusColor = (status: string) => {
  if (status === 'ready')      return { bgcolor: '#d1f4e0', color: '#00783e' };
  if (status === 'failed')     return { bgcolor: '#ffebee', color: '#c62828' };
  return { bgcolor: '#fff3e0', color: '#e65100' };
};

const getReportTypeColor = (type: string) => {
  const colors: Record<string, { bgcolor: string; color: string }> = {
    sales:    { bgcolor: '#e8f5e9', color: '#2e7d32' },
    orders:   { bgcolor: '#e3f2fd', color: '#1565c0' },
    users:    { bgcolor: '#f3e5f5', color: '#7b1fa2' },
    delivery: { bgcolor: '#fff3e0', color: '#e65100' },
    returns:  { bgcolor: '#fff8e1', color: '#f57f17' },
    wallet:   { bgcolor: '#ede7f6', color: '#4527a0' },
  };
  return colors[type] || { bgcolor: '#f1f2f4', color: '#6d7175' };
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-AE', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return dateStr; }
};

// ─────────────────────────────────────────────────────────────────────────────
// Generate Report Dialog
// ─────────────────────────────────────────────────────────────────────────────

interface GenerateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

const GenerateReportDialog: React.FC<GenerateDialogProps> = ({ open, onClose, onSuccess, onError }) => {
  const { data: formatsData, isLoading: formatsLoading } = useListReportFormatsQuery();
  const [generateReport, { isLoading: isGenerating }] = useGenerateReportMutation();

  const [formatId,  setFormatId]  = useState('');
  const [fromDate,  setFromDate]  = useState('');
  const [toDate,    setToDate]    = useState('');
  const [errors,    setErrors]    = useState<Record<string, string>>({});

  const formats = formatsData?.data || [];

  const validate = () => {
    const err: Record<string, string> = {};
    if (!formatId) err.formatId = 'Please select a report format';
    if (!fromDate) err.fromDate = 'From date is required';
    if (!toDate)   err.toDate   = 'To date is required';
    if (fromDate && toDate && fromDate > toDate)
      err.toDate = 'To date must be after from date';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      const result = await generateReport({
        format_id: Number(formatId),
        from_date: fromDate,
        to_date: toDate,
      }).unwrap();
      onSuccess(result.message || 'Report generated successfully!');
      handleClose();
    } catch (err: any) {
      onError(err?.data?.message || err?.message || 'Failed to generate report');
    }
  };

  const handleClose = () => {
    setFormatId(''); setFromDate(''); setToDate(''); setErrors({});
    onClose();
  };

  const selectedFormat = formats.find(f => String(f.id) === formatId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Generate New Report</Typography>
          <IconButton onClick={handleClose} size="small" disabled={isGenerating}><Close /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          <FormControl fullWidth error={!!errors.formatId} disabled={isGenerating || formatsLoading}>
            <InputLabel>Report Format *</InputLabel>
            <Select
              value={formatId}
              onChange={(e: SelectChangeEvent) => { setFormatId(e.target.value); setErrors(p => ({ ...p, formatId: '' })); }}
              label="Report Format *"
            >
              {formats.map(f => (
                <MenuItem key={f.id} value={String(f.id)}>
                  {f.format_name} — <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{f.report_type}</Typography>
                </MenuItem>
              ))}
            </Select>
            {errors.formatId && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.formatId}</Typography>
            )}
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="From Date *" type="date" fullWidth value={fromDate}
                onChange={e => { setFromDate(e.target.value); setErrors(p => ({ ...p, fromDate: '' })); }}
                InputLabelProps={{ shrink: true }}
                error={!!errors.fromDate} helperText={errors.fromDate}
                disabled={isGenerating}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="To Date *" type="date" fullWidth value={toDate}
                onChange={e => { setToDate(e.target.value); setErrors(p => ({ ...p, toDate: '' })); }}
                InputLabelProps={{ shrink: true }}
                error={!!errors.toDate} helperText={errors.toDate}
                disabled={isGenerating}
              />
            </Grid>
          </Grid>

          {selectedFormat && (
            <Box sx={{ p: 1.5, bgcolor: '#f1f2f4', borderRadius: '8px' }}>
              <Typography variant="caption" color="text.secondary">
                Selected format: <strong>{selectedFormat.format_name}</strong>
                {' · '}Export: <strong>{selectedFormat.export_type?.toUpperCase()}</strong>
                {' · '}Fields: <strong>{selectedFormat.fields?.length || 0}</strong>
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={isGenerating} variant="outlined"
          sx={{ textTransform: 'none', borderRadius: '8px', color: '#eeeeee', bgcolor: '#f44336', fontWeight: 500 }}>
          Close
        </Button>
        <Button onClick={handleClose} disabled={isGenerating} variant="outlined"
          sx={{ textTransform: 'none', borderRadius: '8px', color: '#eeeeee', bgcolor: '#F78E12', fontWeight: 500 }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit}
          disabled={isGenerating || !formatId || !fromDate || !toDate}
          sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000' } }}>
          {isGenerating ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              Generating...
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PlayArrow fontSize="small" /> Generate Report
            </Box>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const ReportsList = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen,   setMobileOpen]   = useState(false);
  const [page,         setPage]         = useState(1);
  const [searchStr,    setSearchStr]    = useState('');
  const [tabValue,     setTabValue]     = useState(0);
  const [expandedId,   setExpandedId]   = useState<number | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [deletingId,   setDeletingId]   = useState<number | null>(null);
  const [filterType,   setFilterType]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const { data: listResponse, isLoading, isError, refetch } = useListReportsQuery({
    report_type: filterType  || undefined,
    status:      filterStatus || undefined,
  });
  const [deleteReport, { isLoading: isDeleting }] = useDeleteReportMutation();

  const allReports: GeneratedReport[] = listResponse?.data || [];
  const ITEMS_PER_PAGE = 10;

  const filteredReports = useMemo(() =>
    allReports.filter(r => {
      if (!searchStr) return true;
      const sl = searchStr.toLowerCase();
      return (
        r.format_name?.toLowerCase().includes(sl) ||
        r.report_type?.toLowerCase().includes(sl) ||
        r.generated_by?.toLowerCase().includes(sl)
      );
    }),
  [allReports, searchStr]);

  const totalPages    = Math.ceil(filteredReports.length / ITEMS_PER_PAGE);
  const paginatedList = filteredReports.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const readyCount      = allReports.filter(r => r.status === 'ready').length;
  const generatingCount = allReports.filter(r => r.status === 'generating').length;
  const failedCount     = allReports.filter(r => r.status === 'failed').length;

  const handleDrawerToggle  = () => setMobileOpen(v => !v);
  const handleChangePage    = (_: React.ChangeEvent<unknown>, p: number) => setPage(p);
  const handleSearch        = (e: React.ChangeEvent<HTMLInputElement>) => { setSearchStr(e.target.value); setPage(1); };
  const handleCloseSnackbar = () => setSnackbar(s => ({ ...s, open: false }));
  const handleToggleExpand  = (id: number) => setExpandedId(v => v === id ? null : id);
  const clearFilters        = () => { setSearchStr(''); setFilterType(''); setFilterStatus(''); setPage(1); };

  const handleRefresh = async () => {
    try { await refetch(); setSnackbar({ open: true, message: 'Refreshed!', severity: 'success' }); }
    catch { setSnackbar({ open: true, message: 'Error refreshing', severity: 'error' }); }
  };

  const handleDelete = async (report: GeneratedReport) => {
    if (!window.confirm(`Delete report "${report.format_name}"?`)) return;
    try {
      setDeletingId(report.id);
      const result = await deleteReport({ id: report.id }).unwrap();
      setSnackbar({ open: true, message: result.message || 'Deleted!', severity: 'success' });
      refetch();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.data?.message || err?.message || 'Error deleting.', severity: 'error' });
    } finally { setDeletingId(null); }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
      </Box>
      <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
        <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
      </Drawer>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <Box sx={{ flexShrink: 0 }}><AdminHeader onToggleMenu={handleDrawerToggle} /></Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
          <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>Generated Reports</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={isLoading}
                  sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500 }}>
                  Refresh
                </Button>
                <Button variant="contained" startIcon={<PlayArrow />} onClick={() => setGenerateOpen(true)}
                  sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500 }}>
                  Generate Report
                </Button>
              </Box>
            </Box>

            {!isLoading && !isError && allReports.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <Assessment sx={{ color: '#1565c0', fontSize: 24 }} />,   bg: '#e3f2fd', value: allReports.length, label: 'Total Reports' },
                  { icon: <CheckCircle sx={{ color: '#2e7d32', fontSize: 24 }} />,  bg: '#e8f5e9', value: readyCount,         label: 'Ready' },
                  { icon: <HourglassEmpty sx={{ color: '#e65100', fontSize: 24 }} />, bg: '#fff3e0', value: generatingCount, label: 'Generating' },
                  { icon: <ErrorIcon sx={{ color: '#c62828', fontSize: 24 }} />,    bg: '#ffebee', value: failedCount,        label: 'Failed' },
                ].map(({ icon, bg, value, label }) => (
                  <Grid item xs={12} sm={6} md={3} key={label}>
                    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ bgcolor: bg, borderRadius: '8px', p: 1, display: 'flex' }}>{icon}</Box>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>{value}</Typography>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
              <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}
                  sx={{ px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' }, '& .Mui-selected': { color: '#202223', fontWeight: 600 }, '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 } }}>
                  <Tab label={`All Reports (${filteredReports.length})`} />
                </Tabs>
              </Box>

              <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField size="small" placeholder="Search..." value={searchStr} onChange={handleSearch}
                  sx={{ flexGrow: 1, minWidth: 220, maxWidth: 380, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select value={filterType} onChange={(e: SelectChangeEvent) => { setFilterType(e.target.value); setPage(1); }}
                    displayEmpty sx={{ borderRadius: '8px', bgcolor: 'white' }}>
                    {REPORT_TYPE_FILTER_OPTIONS.map(o => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select value={filterStatus} onChange={(e: SelectChangeEvent) => { setFilterStatus(e.target.value); setPage(1); }}
                    displayEmpty sx={{ borderRadius: '8px', bgcolor: 'white' }}>
                    {REPORT_STATUS_OPTIONS.map(o => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button variant="outlined" startIcon={<FilterList />} onClick={clearFilters}
                  sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500 }}>
                  Clear
                </Button>
              </Box>

              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                  <CircularProgress size={32} sx={{ color: '#008060' }} />
                </Box>
              )}
              {isError && !isLoading && (
                <Box sx={{ p: 3, bgcolor: 'white' }}><Alert severity="error">Failed to load reports.</Alert></Box>
              )}
              {!isLoading && !isError && paginatedList.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                  <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No reports found</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {searchStr || filterType || filterStatus ? 'No results match your filters' : 'Generate your first report to get started'}
                  </Typography>
                  {!searchStr && !filterType && !filterStatus && (
                    <Button variant="contained" startIcon={<PlayArrow />} onClick={() => setGenerateOpen(true)}
                      sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500 }}>
                      Generate First Report
                    </Button>
                  )}
                </Box>
              )}

              {!isLoading && paginatedList.length > 0 && (
                <Box sx={{ bgcolor: 'white' }}>
                  {!isMobile && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '48px 60px 1fr 140px 120px 110px 100px 80px 120px', gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb', alignItems: 'center' }}>
                      {['', 'ID', 'Format Name', 'Report Type', 'Date Range', 'Export', 'Records', 'Status', 'Actions'].map(h => (
                        <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: h === 'Actions' ? 'center' : 'left' }}>{h}</Typography>
                      ))}
                    </Box>
                  )}

                  {paginatedList.map((report) => {
                    const isThisDeleting = deletingId === report.id;

                    return (
                      <Box key={report.id}>
                        <Box
                          onClick={() => handleToggleExpand(report.id)}
                          sx={!isMobile ? {
                            display: 'grid', gridTemplateColumns: '48px 60px 1fr 140px 120px 110px 100px 80px 120px',
                            gap: 1.5, px: 2, py: 2, alignItems: 'center', borderBottom: '1px solid #e1e3e5',
                            '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s ease', cursor: 'pointer',
                          } : { p: 2, borderBottom: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f9fafb' }, cursor: 'pointer' }}
                        >
                          {!isMobile ? (
                            <>
                              <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(report.id); }} sx={{ color: '#6d7175', p: 0 }}>
                                {expandedId === report.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>

                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>#{report.id}</Typography>

                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{report.format_name}</Typography>
                                <Typography variant="caption" color="text.secondary">By {report.generated_by} · {formatDate(report.created_at)}</Typography>
                              </Box>

                              <Chip label={report.report_type} size="small"
                                sx={{ ...getReportTypeColor(report.report_type), fontWeight: 600, fontSize: '11px', height: '24px', textTransform: 'capitalize' }} />

                              <Typography variant="caption" color="text.secondary">
                                {formatDate(report.from_date)} – {formatDate(report.to_date)}
                              </Typography>

                              <Chip icon={getExportIcon(report.export_type)} label={report.export_type?.toUpperCase()}
                                size="small" sx={{ ...getExportColor(report.export_type), fontWeight: 600, fontSize: '11px', height: '24px' }} />

                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                {report.total_records ?? '—'}
                              </Typography>

                              <Chip icon={getStatusIcon(report.status)} label={report.status}
                                size="small" sx={{ ...getStatusColor(report.status), fontWeight: 600, fontSize: '11px', height: '24px', textTransform: 'capitalize' }} />

                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                {report.status === 'ready' && (
                                  <>
                                    {report.download_url_csv && (
                                      <Tooltip title="Download CSV">
                                        <IconButton size="small" component="a" href={report.download_url_csv} target="_blank"
                                          onClick={e => e.stopPropagation()} sx={{ color: '#2e7d32' }}>
                                          <TableChart fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {report.download_url_pdf && (
                                      <Tooltip title="Download PDF">
                                        <IconButton size="small" component="a" href={report.download_url_pdf} target="_blank"
                                          onClick={e => e.stopPropagation()} sx={{ color: '#c62828' }}>
                                          <PictureAsPdf fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </>
                                )}
                                <Tooltip title="Delete">
                                  <IconButton size="small" onClick={e => { e.stopPropagation(); handleDelete(report); }}
                                    disabled={isThisDeleting || isDeleting} sx={{ color: '#d32f2f' }}>
                                    {isThisDeleting ? <CircularProgress size={18} /> : <Delete fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </>
                          ) : (
                            <Stack spacing={1.5}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#202223' }}>{report.format_name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    #{report.id} · By {report.generated_by}
                                  </Typography>
                                </Box>
                                <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(report.id); }} sx={{ color: '#6d7175' }}>
                                  {expandedId === report.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </Box>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                                <Chip label={report.report_type} size="small"
                                  sx={{ ...getReportTypeColor(report.report_type), fontWeight: 600, fontSize: '11px', height: '22px', textTransform: 'capitalize' }} />
                                <Chip icon={getExportIcon(report.export_type)} label={report.export_type?.toUpperCase()} size="small"
                                  sx={{ ...getExportColor(report.export_type), fontWeight: 600, fontSize: '11px', height: '22px' }} />
                                <Chip icon={getStatusIcon(report.status)} label={report.status} size="small"
                                  sx={{ ...getStatusColor(report.status), fontWeight: 600, fontSize: '11px', height: '22px', textTransform: 'capitalize' }} />
                              </Box>
                            </Stack>
                          )}
                        </Box>

                        <Collapse in={expandedId === report.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Report Details</Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                {report.download_url_csv && report.status === 'ready' && (
                                  <Button size="small" startIcon={<TableChart />} component="a" href={report.download_url_csv} target="_blank"
                                    sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500, color: '#2e7d32' }}>
                                    CSV
                                  </Button>
                                )}
                                {report.download_url_pdf && report.status === 'ready' && (
                                  <Button size="small" startIcon={<PictureAsPdf />} component="a" href={report.download_url_pdf} target="_blank"
                                    sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500, color: '#c62828' }}>
                                    PDF
                                  </Button>
                                )}
                                <Button size="small" color="error"
                                  startIcon={isThisDeleting ? <CircularProgress size={16} /> : <Delete />}
                                  onClick={() => handleDelete(report)} disabled={isThisDeleting || isDeleting}
                                  sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500 }}>
                                  Delete
                                </Button>
                              </Box>
                            </Box>

                            <Grid container spacing={2}>
                              {[
                                { label: 'Report Type',   value: report.report_type,   chip: true, chipSx: getReportTypeColor(report.report_type) },
                                { label: 'Export Type',   value: report.export_type?.toUpperCase(), chip: true, chipSx: getExportColor(report.export_type) },
                                { label: 'Status',        value: report.status,        chip: true, chipSx: getStatusColor(report.status) },
                                { label: 'Total Records', value: `${report.total_records ?? '—'} rows`, chip: false },
                                { label: 'From Date',     value: formatDate(report.from_date), chip: false },
                                { label: 'To Date',       value: formatDate(report.to_date),   chip: false },
                                { label: 'Generated By',  value: report.generated_by,  chip: false },
                                { label: 'Created At',    value: formatDate(report.created_at), chip: false },
                              ].map(({ label, value, chip, chipSx }) => (
                                <Grid item xs={6} sm={3} key={label}>
                                  <Card sx={{ p: 1.5, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white', height: '100%' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
                                    {chip && chipSx
                                      ? <Chip label={value} size="small" sx={{ ...(chipSx as any), fontWeight: 600, fontSize: '11px', height: '22px', textTransform: 'capitalize' }} />
                                      : <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{value}</Typography>
                                    }
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Card>

            {totalPages > 1 && !isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination count={totalPages} page={page} onChange={handleChangePage}
                  sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 }, '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }} />
              </Box>
            )}

          </Box>
        </Box>
      </Box>

      <GenerateReportDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onSuccess={(msg) => { setSnackbar({ open: true, message: msg, severity: 'success' }); refetch(); }}
        onError={(msg)   => setSnackbar({ open: true, message: msg, severity: 'error' })}
      />

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportsList;